// src/features/analytics/SavedQueryDialog.tsx
// Create / edit a saved query. CreateSavedQuery, UpdateSavedQuery.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Input, Textarea, Button, Alert } from '@react/components';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { analyticsApi } from '@core/api';
import type { AvailableDataSource, DataSourceConfig, SavedQuery } from '@core/types/analytics';
import { AnalyticsModal } from './Modal';
import { DataSourceConfigEditor } from './DataSourceConfigEditor';
import { errMessage, makeEntity, type ScopeIds } from './helpers';

const EMPTY_DATA_SOURCE: DataSourceConfig = {
  product_slug: '',
  entity_type: '',
  metrics: [],
  filters: [],
  group_by: [],
  order_by: '',
  order_direction: 'desc',
  limit: 0,
};

interface SavedQueryDialogProps {
  open: boolean;
  onClose: () => void;
  scope: ScopeIds;
  dataSources: AvailableDataSource[];
  query?: SavedQuery | null;
  onSaved: (query: SavedQuery) => void;
}

export function SavedQueryDialog({ open, onClose, scope, dataSources, query, onSaved }: SavedQueryDialogProps) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [shared, setShared] = useState(false);
  const [definition, setDefinition] = useState<DataSourceConfig>(EMPTY_DATA_SOURCE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName(query?.name ?? '');
    setDescription(query?.description ?? '');
    setShared(query?.is_shared ?? false);
    setDefinition(query?.query_definition ?? EMPTY_DATA_SOURCE);
  }, [open, query]);

  const save = async () => {
    if (!name.trim()) {
      setError(t('analytics.queries.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (query?.entity?.id) {
        const res = await analyticsApi.UpdateSavedQuery(query.entity.id, {
          query: { ...query, name: name.trim(), description, is_shared: shared, query_definition: definition },
        });
        if (res.query) onSaved(res.query);
      } else {
        const res = await analyticsApi.CreateSavedQuery({
          entity: makeEntity('', 'saved_query', scope),
          name: name.trim(),
          description,
          query_definition: definition,
          is_shared: shared,
        });
        if (res.query) onSaved(res.query);
      }
      onClose();
    } catch (e) {
      setError(errMessage(e, t('analytics.queries.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnalyticsModal
      open={open}
      onClose={onClose}
      title={query ? t('analytics.queries.edit') : t('analytics.queries.create')}
      widthClass="max-w-3xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      )}
      <div className="space-y-4">
        <Input label={t('analytics.queries.name')} value={name} onChange={(e) => setName(e.target.value)} required />
        <Textarea label={t('analytics.queries.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        <ToggleSwitch label={t('analytics.queries.shared')} description={t('analytics.queries.sharedHelp')} checked={shared} onChange={setShared} />
        <DataSourceConfigEditor value={definition} onChange={setDefinition} dataSources={dataSources} />
      </div>
    </AnalyticsModal>
  );
}
